import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  FileText,
  Search,
  RefreshCw,
  Loader2,
  Plus,
  Pencil,
  Trash2,
  User,
  Calendar,
  Filter,
} from "lucide-react";
import { toast } from "sonner";

export default function AuditLogsPage() {
  const { api } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    action: "",
    entity_type: "",
    start_date: "",
    end_date: "",
    limit: 50,
    skip: 0,
  });

  useEffect(() => {
    fetchMetadata();
    fetchLogs();
  }, []);

  const fetchMetadata = async () => {
    try {
      const [actionsRes, typesRes] = await Promise.all([
        api.get("/audit-logs/actions"),
        api.get("/audit-logs/entity-types"),
      ]);
      setActions(actionsRes.data);
      setEntityTypes(typesRes.data);
    } catch (error) {
      // Ignore if no permissions
    }
  };

  const fetchLogs = async (newFilters = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (newFilters.action) params.append("action", newFilters.action);
      if (newFilters.entity_type) params.append("entity_type", newFilters.entity_type);
      if (newFilters.start_date) params.append("start_date", newFilters.start_date);
      if (newFilters.end_date) params.append("end_date", newFilters.end_date);
      params.append("limit", newFilters.limit);
      params.append("skip", newFilters.skip);
      
      const response = await api.get(`/audit-logs?${params.toString()}`);
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Access denied. Admin or Manager role required.");
      } else {
        toast.error("Failed to load audit logs");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    // Convert "all" back to empty string for API
    const actualValue = value === "all" ? "" : value;
    const newFilters = { ...filters, [key]: actualValue, skip: 0 };
    setFilters(newFilters);
    fetchLogs(newFilters);
  };

  const clearFilters = () => {
    const newFilters = {
      action: "",
      entity_type: "",
      start_date: "",
      end_date: "",
      limit: 50,
      skip: 0,
    };
    setFilters(newFilters);
    fetchLogs(newFilters);
  };

  const loadMore = () => {
    const newFilters = { ...filters, skip: filters.skip + filters.limit };
    setFilters(newFilters);
    fetchLogs(newFilters);
  };

  const getActionBadge = (action) => {
    const variants = {
      create: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
      update: "bg-blue-500/10 text-blue-500 border-blue-500/20",
      delete: "bg-red-500/10 text-red-500 border-red-500/20",
      login: "bg-purple-500/10 text-purple-500 border-purple-500/20",
    };
    const icons = {
      create: Plus,
      update: Pencil,
      delete: Trash2,
      login: User,
    };
    const Icon = icons[action] || FileText;
    
    return (
      <Badge className={`${variants[action] || "bg-slate-500/10 text-slate-500"} gap-1`} variant="outline">
        <Icon className="h-3 w-3" />
        {action}
      </Badge>
    );
  };

  const getEntityTypeBadge = (type) => {
    return (
      <Badge variant="secondary" className="capitalize">
        {type.replace(/_/g, " ")}
      </Badge>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <FileText className="h-8 w-8 text-accent" />
            Audit Logs
          </h1>
          <p className="text-muted-foreground">
            Track all user actions and system changes
          </p>
        </div>
        <Button onClick={() => fetchLogs()} disabled={loading}>
          {loading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Action</label>
              <Select
                value={filters.action || "all"}
                onValueChange={(value) => handleFilterChange("action", value)}
              >
                <SelectTrigger data-testid="filter-action">
                  <SelectValue placeholder="All actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All actions</SelectItem>
                  {actions.map((action) => (
                    <SelectItem key={action} value={action}>
                      {action}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Entity Type</label>
              <Select
                value={filters.entity_type || "all"}
                onValueChange={(value) => handleFilterChange("entity_type", value)}
              >
                <SelectTrigger data-testid="filter-entity-type">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {entityTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Start Date</label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange("start_date", e.target.value)}
                data-testid="filter-start-date"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">End Date</label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange("end_date", e.target.value)}
                data-testid="filter-end-date"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">&nbsp;</label>
              <Button variant="outline" onClick={clearFilters} className="w-full">
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Activity Log</CardTitle>
          <CardDescription>
            {total > 0 ? `Showing ${logs.length} of ${total} entries` : "No entries found"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading && logs.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">No audit logs found</p>
              <p className="text-sm">Activity will appear here when users perform actions</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Entity Type</TableHead>
                    <TableHead>Entity Name</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} data-testid={`log-row-${log.id}`}>
                      <TableCell className="font-mono text-xs whitespace-nowrap">
                        {new Date(log.timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="h-6 w-6 rounded-full bg-accent/20 flex items-center justify-center">
                            <User className="h-3 w-3 text-accent" />
                          </div>
                          <span className="text-sm truncate max-w-[150px]" title={log.user_email}>
                            {log.user_email}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>{getActionBadge(log.action)}</TableCell>
                      <TableCell>{getEntityTypeBadge(log.entity_type)}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {log.entity_name || "-"}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs max-w-[200px]">
                        {log.details && Object.keys(log.details).length > 0 ? (
                          <code className="text-xs bg-muted px-1 py-0.5 rounded">
                            {JSON.stringify(log.details).substring(0, 50)}
                            {JSON.stringify(log.details).length > 50 ? "..." : ""}
                          </code>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {logs.length < total && (
                <div className="flex justify-center mt-4">
                  <Button variant="outline" onClick={loadMore} disabled={loading}>
                    {loading ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    Load More
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
